import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, getForSession, postForSession } from "./client";
import { useAuth } from "../auth/AuthProvider";
import { usePollingInterval } from "../realtime/RealtimeProvider";
import type {
  FlowPreview,
  FlowScenarioSpec,
  ProcessTemplate,
  ProcessTemplateCreate,
  ProcessTemplateUpdate,
  ProcurementObject,
  ProcurementObjectCreate,
  ProcurementObjectUpdate,
  TemplateStep,
  AnalyticsFilter,
  AnalyticsSummary,
  ApprovalRoute,
  ApprovalRouteCreate,
  ApprovalRouteReorder,
  ApprovalRouteStepCreate,
  ApprovalRouteStepUpdate,
  ApprovalRouteSummary,
  ApprovalRouteUpdate,
  AuditEvent,
  ChatMessage,
  ChatResponse,
  ContractResultRequest,
  LlmModelRoute,
  LlmModelRouteUpdate,
  LlmProvider,
  LlmProviderCreate,
  LlmProviderTestResult,
  LlmProviderUpdate,
  ManualConvertRequest,
  SplitItemsOutRequest,
  SplitToProcurementsRequest,
  Page,
  PriceHistoryResponse,
  PriceVarianceResponse,
  Process,
  ProcessSummary,
  ProductCategory,
  ProductCategoryCreate,
  ProductCategoryUpdate,
  RequestCreate,
  RequestTypeRouting,
  RequestTypeRoutingCreate,
  RequestTypeRoutingUpdate,
  RisksResponse,
  SavingsResponse,
  SpendResponse,
  StepDecisionRequest,
  StockCheckResultRequest,
  Supplier,
  SupplierAttachment,
  SupplierChatUnread,
  SupplierTag,
  SupplierTagInput,
  Unit,
  UnitCreate,
  UnitUpdate,
  UserRole,
} from "../types";

export const PROCESSES_KEY = ["processes"] as const;
export const EVENTS_KEY = ["events"] as const;
export const SUPPLIERS_KEY = ["suppliers"] as const;

export interface ProcessListParams {
  status?: string;
  current_stage?: string;
  priority?: string;
  process_type?: string;
  supplier_id?: string;
  approver_id?: string;
  q?: string;
  sort?: string;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  show_archived?: boolean;
  include_completed?: boolean;
  include_requests?: boolean;
  mine_only?: boolean;
  sla_overdue?: boolean;
  product_category_code?: string;
  department?: string;
  /** Показать закупки прогонов e2e («E2E …»). По умолчанию сервер их прячет. */
  include_test?: boolean;
  /** Разрез по юрлицу: за кого закупка. */
  legal_entity_id?: string;
  object_id?: string;
}

export function processListQuery(params: ProcessListParams = {}): string {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.current_stage) search.set("current_stage", params.current_stage);
  if (params.priority) search.set("priority", params.priority);
  if (params.process_type) search.set("process_type", params.process_type);
  if (params.supplier_id) search.set("supplier_id", params.supplier_id);
  if (params.approver_id) search.set("approver_id", params.approver_id);
  if (params.q) search.set("q", params.q);
  if (params.sort) search.set("sort", params.sort);
  if (params.order) search.set("order", params.order);
  if (params.department) search.set("department", params.department);
  if (params.show_archived) search.set("show_archived", "true");
  if (params.include_completed) search.set("include_completed", "true");
  if (params.include_requests) search.set("include_requests", "true");
  if (params.mine_only) search.set("mine_only", "true");
  if (params.sla_overdue) search.set("sla_overdue", "true");
  if (params.include_test) search.set("include_test", "true");
  if (params.legal_entity_id) search.set("legal_entity_id", params.legal_entity_id);
  if (params.object_id) search.set("object_id", params.object_id);
  if (params.product_category_code) search.set("product_category_code", params.product_category_code);
  search.set("limit", String(params.limit ?? 50));
  search.set("offset", String(params.offset ?? 0));
  return `/processes?${search}`;
}

export function useProcesses(
  params: ProcessListParams = {},
  options: { enabled?: boolean } = {},
) {
  const poll = usePollingInterval(5_000);
  const enabled = options.enabled ?? true;
  return useQuery({
    queryKey: ["processes", params],
    queryFn: () => api.get<Page<ProcessSummary>>(processListQuery(params)),
    refetchInterval: enabled ? poll : false,
    enabled,
  });
}

export function useProcess(id: string | undefined) {
  const poll = usePollingInterval(5_000);
  return useQuery({
    queryKey: ["process", id],
    queryFn: () => api.get<Process>(`/processes/${id}`),
    enabled: !!id,
    refetchInterval: poll,
  });
}

export function useSubmitApproval(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (decision: { decision: string; comment?: string }) =>
      api.post<Process>(`/processes/${id}/approval`, decision),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", id] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

/** Убрать процесс из закэшированного списка, не зная заранее его формы. */
function withoutProcess(old: unknown, id: string): unknown {
  if (!old || typeof old !== "object") return old;
  const page = old as { items?: unknown };
  if (Array.isArray(page.items)) {
    return {
      ...page,
      items: page.items.filter((p: { process_id?: string }) => p?.process_id !== id),
    };
  }
  const infinite = old as { pages?: unknown };
  if (Array.isArray(infinite.pages)) {
    return { ...infinite, pages: infinite.pages.map((pg) => withoutProcess(pg, id)) };
  }
  return old;
}

export function useSubmitApprovalAction(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      action: "approve" | "reject" | "clarify" | "reassign" | "return_for_changes";
      comment?: string;
      new_assignee_id?: string;
    }) =>
      api.post<Process>(`/processes/${id}/approval-action`, payload),
    // Оптимистично убираем процесс из очереди согласований до ответа сервера —
    // иначе после решения бейдж/строка висят старыми до refetch.
    onMutate: async (payload) => {
      if (payload.action !== "approve" && payload.action !== "reject") return;
      await qc.cancelQueries({ queryKey: PROCESSES_KEY });
      const previous = qc.getQueriesData<unknown>({ queryKey: PROCESSES_KEY });
      // Под этим ключом лежат не только страницы списка: постраничный список
      // («pages»), счётчик непрочитанных чатов и прочее. Прежний код брал
      // `old.items` у всех подряд и падал на `.filter` ДО отправки запроса —
      // у закупщика «Согласовать» не делал ничего. Трогаем только списки.
      qc.setQueriesData<unknown>({ queryKey: PROCESSES_KEY }, (old: unknown) =>
        withoutProcess(old, id),
      );
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      // откат оптимистичного обновления
      for (const [key, data] of ctx?.previous ?? []) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["process", id] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useUpdateProcess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<{
      title: string;
      description: string;
      priority: "low" | "normal" | "high" | "urgent";
      department: string;
      delivery_deadline: string | null;
      offers_deadline: string | null;
      delivery_address: string;
      legal_entity_id: string | null;
      budget_estimated: number | null;
      category_code: string;
      approver_id: string;
      assignee_id: string;
      supplier_id: string | null;
    }>) =>
      api.patch<Process>(
        `/processes/${id}`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", id] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useAddItem(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      sku?: string;
      qty: number;
      unit?: string;
      estimated_price?: number;
      comment?: string;
      position?: number;
    }) => api.post(`/processes/${processId}/items`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: ["request", processId] });
    },
  });
}

export function useUpdateItem(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, ...body }: { itemId: string } & Record<string, unknown>) =>
      api.patch(`/processes/${processId}/items/${itemId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: ["request", processId] });
    },
  });
}

export function useDeleteItem(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.del(`/processes/${processId}/items/${itemId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: ["request", processId] });
    },
  });
}

export function useUploadAttachment(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: { file: File; comment?: string; docKind?: string } | File,
    ) => {
      const fd = new FormData();
      const file = body instanceof File ? body : body.file;
      const comment = body instanceof File ? undefined : body.comment;
      const docKind = body instanceof File ? undefined : body.docKind;
      fd.append("file", file);
      if (comment) fd.append("comment", comment);
      if (docKind) fd.append("doc_kind", docKind);
      const res = await fetch(`/api/v1/processes/${processId}/attachments`, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || res.statusText);
      }
      return res.json();
    },
    onSuccess: () => {
      // process — страница процесса, request — страница заявки (разные ключи!)
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: ["request", processId] });
    },
  });
}

export function useDeleteAttachment(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      api.del(`/processes/${processId}/attachments/${attachmentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: ["request", processId] });
    },
  });
}

export function useAddComment(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      body: string;
      author_id?: string;
      kind?: string;
      visibility?: string;
      thread_supplier_id?: string;
    }) =>
      api.post(`/processes/${processId}/comments`, {
        body: body.body,
        author_id: body.author_id ?? "user-1",
        author_type: "user",
        kind: body.kind ?? "comment",
        visibility: body.visibility ?? "internal",
        ...(body.thread_supplier_id
          ? { thread_supplier_id: body.thread_supplier_id }
          : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: ["request", processId] });
    },
  });
}

/** Непрочитанные сообщения от поставщиков (бейдж в меню). */
export function useUnreadSupplierChats() {
  const poll = usePollingInterval(15_000);
  return useQuery({
    queryKey: ["processes", "unread-supplier-chats"],
    queryFn: () => api.get<SupplierChatUnread>("/processes/unread-supplier-chats"),
    refetchInterval: poll,
  });
}

export function useMarkSupplierChatRead(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/processes/${processId}/comments/mark-supplier-read`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["processes", "unread-supplier-chats"] }),
  });
}

export function useCancelProcess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      api.post<Process>(`/processes/${id}/cancel?reason=${encodeURIComponent(body.reason)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", id] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useDeleteProcess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.del<void>(`/processes/${id}?reason=${encodeURIComponent(reason)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", id] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
      qc.removeQueries({ queryKey: ["process", id] });
    },
  });
}

export function useResumeProcess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<Process>(`/processes/${id}/resume`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", id] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useCreateProcess() {
  const qc = useQueryClient();
  const { token } = useAuth();
  return useMutation({
    mutationFn: (body: {
      process_type: string;
      title: string;
      description?: string | null;
      department?: string | null;
      product_category_code?: string | null;
      priority?: "low" | "normal" | "high" | "urgent";
      budget_estimated?: number | null;
      delivery_deadline?: string | null;
      delivery_address?: string | null;
      legal_entity_id?: string | null;
      category_code?: string;
      supplier_id?: string;
      sbis_document?: Record<string, unknown>;
      items?: Array<{
        name: string;
        qty?: number;
        unit?: string;
        estimated_price?: number | null;
      }>;
    }) => postForSession<Process>("/processes", body, token),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROCESSES_KEY }),
  });
}

export function useEvents(params: { process_id?: string; limit?: number; offset?: number } = {}) {
  const poll = usePollingInterval(5_000);
  const search = new URLSearchParams();
  if (params.process_id) search.set("process_id", params.process_id);
  search.set("limit", String(params.limit ?? 100));
  search.set("offset", String(params.offset ?? 0));
  return useQuery({
    queryKey: [...EVENTS_KEY, params],
    queryFn: () => api.get<Page<AuditEvent>>(`/events${search.toString() ? `?${search}` : ""}`),
    refetchInterval: poll,
  });
}

export function useSuppliers(
  /** include_test — только для страницы «Поставщики»: ей нужен переключатель
   *  «тестовые (N)». Формы и выпадающие списки тестовых не получают. */
  params: { q?: string; active_only?: boolean; category?: string; include_test?: boolean } = {},
  options?: { placeholderData?: (prev: Supplier[] | undefined) => Supplier[] | undefined },
) {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.active_only !== undefined) search.set("active_only", String(params.active_only));
  if (params.include_test) search.set("include_test", "true");
  if (params.category) search.set("category", params.category);
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, params],
    queryFn: () =>
      api.get<Supplier[]>(`/suppliers${search.toString() ? `?${search}` : ""}`),
    placeholderData: options?.placeholderData,
  });
}

export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: ["supplier", id],
    queryFn: () => api.get<Supplier>(`/suppliers/${id}`),
    enabled: !!id,
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      email?: string;
      phone?: string;
      inn: string; // обязательное поле
      categories?: string[];
    }) => api.post<Supplier>("/suppliers", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }),
  });
}

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: Record<string, unknown> }) =>
      api.patch<Supplier>(`/suppliers/${args.id}`, args.body),
    onSuccess: (_d, args) => {
      qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
      qc.invalidateQueries({ queryKey: ["supplier", args.id] });
    },
  });
}

export function useDeactivateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Supplier>(`/suppliers/${id}/deactivate`),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
      qc.invalidateQueries({ queryKey: ["supplier", id] });
    },
  });
}

export function useActivateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Supplier>(`/suppliers/${id}/activate`),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
      qc.invalidateQueries({ queryKey: ["supplier", id] });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/suppliers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }),
  });
}

export function useSupplierAttachments(supplierId: string | undefined) {
  return useQuery({
    queryKey: ["supplier-attachments", supplierId],
    queryFn: () => api.get<SupplierAttachment[]>(`/suppliers/${supplierId}/attachments`),
    enabled: !!supplierId,
  });
}

export function useUploadSupplierAttachment(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, doc_kind, notes }: { file: File; doc_kind: string; notes?: string }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_kind", doc_kind);
      if (notes) fd.append("notes", notes);
      const res = await fetch(`/api/v1/suppliers/${supplierId}/attachments`, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("rus_ii_token")}` },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || res.statusText);
      }
      return (await res.json()) as SupplierAttachment;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["supplier-attachments", supplierId] }),
  });
}

export function useDeleteSupplierAttachment(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => api.del<void>(`/suppliers/${supplierId}/attachments/${attachmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["supplier-attachments", supplierId] }),
  });
}

// ---- теги поставщика (категории + вид деятельности) ----

export const SUPPLIER_TAGS_KEY = ["supplier-tags"] as const;

export function useSupplierTags(supplierId: string | undefined) {
  return useQuery({
    queryKey: [...SUPPLIER_TAGS_KEY, supplierId],
    queryFn: () => api.get<SupplierTag[]>(`/suppliers/${supplierId}/tags`),
    enabled: !!supplierId,
  });
}

export function usePutSupplierTags(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tags: SupplierTagInput[]) =>
      api.put<SupplierTag[]>(`/suppliers/${supplierId}/tags`, { tags }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...SUPPLIER_TAGS_KEY, supplierId] });
      qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
      qc.invalidateQueries({ queryKey: ["supplier", supplierId] });
    },
  });
}

// ===== Departments =====

import type { Department, LegalEntity, LegalEntityCreate, LegalEntityUpdate } from "../types";

export type { Department };

export const DEPARTMENTS_KEY = ["departments"] as const;

export function useDepartments(
  params: { active_only?: boolean; q?: string; include_test?: boolean } = {},
) {
  const search = new URLSearchParams();
  if (params.active_only) search.set("active_only", "true");
  if (params.include_test) search.set("include_test", "true");
  if (params.q) search.set("q", params.q);
  return useQuery({
    queryKey: [...DEPARTMENTS_KEY, params],
    queryFn: () => api.get<Department[]>(`/departments${search.toString() ? `?${search}` : ""}`),
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      code: string;
      name: string;
      head_user_id?: string;
      legal_entity_id?: string | null;
      object_id?: string | null;
      description?: string;
    }) => api.post<Department>("/departments", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY }),
  });
}

export function useUpdateDepartment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<{
      code: string;
      name: string;
      head_user_id: string | null;
      legal_entity_id: string | null;
      object_id: string | null;
      description: string;
      is_active: boolean;
      budget_mode: "warn" | "block";
    }>) => api.patch<Department>(`/departments/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY }),
  });
}

/** Включить или отключить отдел. Отдельный хук, потому что `useUpdateDepartment`
 *  берёт id при создании, а переключатель стоит в каждой строке списка. */
export function useToggleDepartmentActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.patch<Department>(`/departments/${id}`, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY }),
  });
}

const LEGAL_ENTITIES_KEY = ["legal-entities"];

/** Справочник юрлиц. Читают все внутренние роли: поле выбирают в заявке. */
export function useLegalEntities(params: { active_only?: boolean } = {}) {
  const search = new URLSearchParams();
  if (params.active_only) search.set("active_only", "true");
  return useQuery({
    queryKey: [...LEGAL_ENTITIES_KEY, params],
    queryFn: () =>
      api.get<LegalEntity[]>(`/legal-entities${search.toString() ? `?${search}` : ""}`),
  });
}

export function useCreateLegalEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LegalEntityCreate) => api.post<LegalEntity>("/legal-entities", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY });
      // Отделы показывают название юрлица — их список тоже устарел.
      void qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY });
    },
  });
}

export function useUpdateLegalEntity(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LegalEntityUpdate) =>
      api.patch<LegalEntity>(`/legal-entities/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY });
      void qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY });
    },
  });
}

export function useToggleLegalEntityActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.patch<LegalEntity>(`/legal-entities/${id}`, { is_active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY });
      void qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY });
    },
  });
}

export function useDeleteLegalEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/legal-entities/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY });
      void qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY });
    },
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/departments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY }),
  });
}

// ===== Tenders =====
import type { TenderCampaign, TenderCampaignSummary, TenderCandidate, TenderOffer, ComparisonRow } from "../types";

export type { TenderCampaign, TenderCampaignSummary, TenderCandidate, TenderOffer, ComparisonRow };

export const TENDERS_KEY = ["tenders"] as const;

export function useTenders(params: { process_id?: string; status?: string } = {}) {
  const search = new URLSearchParams();
  if (params.process_id) search.set("process_id", params.process_id);
  if (params.status) search.set("status", params.status);
  return useQuery({
    queryKey: [...TENDERS_KEY, params],
    queryFn: () => api.get<TenderCampaignSummary[]>(`/tenders${search.toString() ? `?${search}` : ""}`),
  });
}

export function useTender(id: string | undefined) {
  return useQuery({
    queryKey: ["tender", id],
    queryFn: () => api.get<TenderCampaign>(`/tenders/${id}`),
    enabled: !!id,
    refetchInterval: 10_000,
  });
}

export function useTenderComparison(id: string | undefined) {
  return useQuery({
    queryKey: ["tender-comparison", id],
    queryFn: () => api.get<ComparisonRow[]>(`/tenders/${id}/comparison`),
    enabled: !!id,
    refetchInterval: 10_000,
  });
}

export function useCreateTender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      process_id: string;
      query: string;
      description?: string;
      budget_estimated?: number;
      deadline_at: string;
      notes?: string;
    }) => api.post<TenderCampaign>("/tenders", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: TENDERS_KEY }),
  });
}

export function useStartTenderSearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, queries }: { id: string; queries?: string[] }) => {
      const search = queries && queries.length ? `?${queries.map((q) => `queries=${encodeURIComponent(q)}`).join("&")}` : "";
      return api.post<{ campaign_id: string; found: number; candidates: TenderCandidate[] }>(
        `/tenders/${id}/start-search${search}`,
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["tender", vars.id] });
      qc.invalidateQueries({ queryKey: ["tender-comparison", vars.id] });
      qc.invalidateQueries({ queryKey: TENDERS_KEY });
    },
  });
}

export function useInviteCandidates(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      candidate_ids: string[];
      subject: string;
      body: string;
      account_id: string;
    }) => api.post<TenderCampaign>(`/tenders/${tenderId}/invite`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tender", tenderId] });
      qc.invalidateQueries({ queryKey: ["tender-comparison", tenderId] });
    },
  });
}

export function useAwardTender(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { candidate_id: string }) =>
      api.post<TenderCampaign>(`/tenders/${tenderId}/award`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tender", tenderId] });
      qc.invalidateQueries({ queryKey: TENDERS_KEY });
    },
  });
}

export function useCancelTender(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<TenderCampaign>(`/tenders/${tenderId}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tender", tenderId] });
      qc.invalidateQueries({ queryKey: TENDERS_KEY });
    },
  });
}

export function useDeleteCandidate(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: string) =>
      api.del<void>(`/tenders/${tenderId}/candidates/${candidateId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tender", tenderId] });
      qc.invalidateQueries({ queryKey: ["tender-comparison", tenderId] });
    },
  });
}

// ===== Categories =====

export interface Category {
  category_code: string;
  category_name: string | null;
  routing_type: string;
  fixed_supplier_id: string | null;
  contract_id: string | null;
  synonyms: string[];
}

// ===== Supplier Category Bindings (новая модель, заменяет CategoryRouting.fixed_supplier_id) =====

export interface SupplierCategoryBinding {
  supplier_id: string;
  category_code: string;
  category_name: string | null;
  is_preferred: boolean;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export const SUPPLIER_BINDINGS_KEY = ["supplier-category-bindings"] as const;

export function useSupplierBindings(supplierId: string | undefined) {
  return useQuery({
    queryKey: [...SUPPLIER_BINDINGS_KEY, supplierId],
    queryFn: () =>
      api.get<SupplierCategoryBinding[]>(
        `/suppliers/${supplierId}/category-bindings`,
      ),
    enabled: !!supplierId,
  });
}

export function usePreferredSupplierForCategory(
  categoryCode: string | undefined,
) {
  return useQuery({
    queryKey: ["category-bindings", "preferred", categoryCode],
    queryFn: () =>
      api.get<SupplierCategoryBinding | null>(
        `/category-bindings/preferred/${categoryCode}`,
      ),
    enabled: !!categoryCode,
  });
}

export function useAddSupplierBinding(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      category_code: string;
      is_preferred?: boolean;
      note?: string | null;
    }) =>
      api.post<SupplierCategoryBinding>(
        `/suppliers/${supplierId}/category-bindings`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SUPPLIER_BINDINGS_KEY });
      qc.invalidateQueries({ queryKey: ["category-bindings"] });
      qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
  });
}

export function useUpdateSupplierBinding(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryCode,
      payload,
    }: {
      categoryCode: string;
      payload: { is_preferred?: boolean; note?: string | null };
    }) =>
      api.patch<SupplierCategoryBinding>(
        `/suppliers/${supplierId}/category-bindings/${categoryCode}`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SUPPLIER_BINDINGS_KEY });
      qc.invalidateQueries({ queryKey: ["category-bindings"] });
      qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
  });
}

export function useDeleteSupplierBinding(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (categoryCode: string) =>
      api.del<void>(
        `/suppliers/${supplierId}/category-bindings/${categoryCode}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SUPPLIER_BINDINGS_KEY });
      qc.invalidateQueries({ queryKey: ["category-bindings"] });
      qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
  });
}


// ===== AI Routes (routing_types — шаблоны сценариев ИИ) =====
export interface AiRoute {
  routing_type: string;
  reconciliation_doc: string | null;
  approval_mode: string;
  is_active: boolean;
  comment: string | null;
}

export const AI_ROUTES_KEY = ["ai-routes"] as const;

export function useAiRoutes(params: { active_only?: boolean } = {}) {
  const search = new URLSearchParams();
  if (params.active_only) search.set("active_only", "true");
  return useQuery({
    queryKey: [...AI_ROUTES_KEY, params],
    queryFn: () =>
      api.get<AiRoute[]>(`/ai-routes${search.toString() ? `?${search}` : ""}`),
  });
}

export function useUpdateAiRoute(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      reconciliation_doc?: string | null;
      approval_mode?: string;
      is_active?: boolean;
      comment?: string | null;
    }) => api.patch<AiRoute>(`/ai-routes/${code}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: AI_ROUTES_KEY }),
  });
}

// ===== LLM settings (admin): провайдеры и модели по задачам =====

export const LLM_PROVIDERS_KEY = ["llm-providers"] as const;
export const LLM_MODEL_ROUTES_KEY = ["llm-model-routes"] as const;

export function useLlmProviders() {
  return useQuery({
    queryKey: LLM_PROVIDERS_KEY,
    queryFn: () => api.get<LlmProvider[]>("/llm/providers"),
  });
}

export function useCreateLlmProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LlmProviderCreate) =>
      api.post<LlmProvider>("/llm/providers", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: LLM_PROVIDERS_KEY }),
  });
}

export function useUpdateLlmProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: LlmProviderUpdate }) =>
      api.patch<LlmProvider>(`/llm/providers/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: LLM_PROVIDERS_KEY }),
  });
}

export function useDeleteLlmProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/llm/providers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LLM_PROVIDERS_KEY });
      qc.invalidateQueries({ queryKey: LLM_MODEL_ROUTES_KEY });
    },
  });
}

export function useTestLlmProvider() {
  return useMutation({
    mutationFn: ({ id, model }: { id: string; model?: string }) =>
      api.post<LlmProviderTestResult>(`/llm/providers/${id}/test`, { model: model ?? null }),
  });
}

export function useLlmModelRoutes() {
  return useQuery({
    queryKey: LLM_MODEL_ROUTES_KEY,
    queryFn: () => api.get<LlmModelRoute[]>("/llm/model-routes"),
  });
}

export function useUpdateLlmModelRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskType, payload }: { taskType: string; payload: LlmModelRouteUpdate }) =>
      api.patch<LlmModelRoute>(`/llm/model-routes/${taskType}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: LLM_MODEL_ROUTES_KEY }),
  });
}

export function useResetLlmModelRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskType: string) => api.del(`/llm/model-routes/${taskType}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: LLM_MODEL_ROUTES_KEY }),
  });
}

export const CATEGORIES_KEY = ["categories-deprecated"] as const;

// DEPRECATED: useCategories/useCreateCategory/useUpdateCategory/useDeleteCategory
// используют устаревший CategoryRouting. Для нового кода:
//   - /product-categories        → useProductCategories / useCreateProductCategory / ...
//   - /suppliers/{id}/category-bindings → useSupplierBindings / useAddSupplierBinding / ...
//   - /category-bindings/preferred/{code} → usePreferredSupplierForCategory
// Эти хуки оставлены для backward-compat со старыми страницами.
export function useCategories(params: { routing_type?: string; active_only?: boolean } = {}) {
  const search = new URLSearchParams();
  if (params.routing_type) search.set("routing_type", params.routing_type);
  if (params.active_only) search.set("active_only", "true");
  return useQuery({
    queryKey: [...CATEGORIES_KEY, params],
    queryFn: () => api.get<Category[]>(`/categories${search.toString() ? `?${search}` : ""}`),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      category_code: string;
      category_name?: string;
      routing_type: string;
      fixed_supplier_id?: string;
      synonyms?: string[];
    }) => api.post<Category>("/categories", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: CATEGORIES_KEY }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, ...body }: { code: string } & Partial<Omit<Category, "category_code">>) =>
      api.patch<Category>(`/categories/${encodeURIComponent(code)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: CATEGORIES_KEY }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.del<void>(`/categories/${encodeURIComponent(code)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: CATEGORIES_KEY }),
  });
}

// ===== Chat =====
export function useChat() {
  return useMutation({
    mutationFn: (body: { message: string; session_id: string | null; history?: ChatMessage[] }) =>
      api.post<ChatResponse>("/chat", body),
  });
}

// ===== Email =====

import type { EmailAccount, EmailMessageSummary, EmailMessageFull, EmailProtocol, MailCleanupPreview, MailCleanupResult } from "../types";

export type { EmailAccount, EmailMessageSummary, EmailMessageFull, EmailAttachment, EmailProtocol } from "../types";

export interface EmailCreateRequestFromEmail {
  process_type?: string;
  category_code?: string;
  title?: string;
  description?: string;
  priority?: "low" | "normal" | "high" | "urgent";
  department?: string;
  copy_attachments?: boolean;
}

export function useEmailAccounts() {
  return useQuery({
    queryKey: ["email-accounts"],
    queryFn: () => api.get<EmailAccount[]>("/email/accounts"),
  });
}

export function useCreateEmailAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      label: string;
      protocol: EmailProtocol;
      imap_host: string;
      imap_port: number;
      smtp_host: string;
      smtp_port: number;
      username: string;
      password: string;
      from_name?: string;
      enabled?: boolean;
    }) => api.post<EmailAccount>("/email/accounts", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-accounts"] }),
  });
}

export function useUpdateEmailAccount(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<{
      label: string;
      imap_host: string;
      imap_port: number;
      imap_ssl: boolean;
      smtp_host: string;
      smtp_port: number;
      smtp_ssl: boolean;
      username: string;
      password: string;
      from_name: string;
      enabled: boolean;
    }>) => api.patch<EmailAccount>(`/email/accounts/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-accounts"] }),
  });
}

export function useDeleteEmailAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/email/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-accounts"] }),
  });
}

export function useTestEmailAccount() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{
        ok: boolean;
        imap_ok: boolean;
        smtp_ok: boolean;
        imap_message: string | null;
        smtp_message: string | null;
        server_info?: { mailbox_messages?: number };
      }>(`/email/accounts/${id}/test`),
  });
}

export function usePollEmailNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ new_messages: number }>(`/email/accounts/${id}/poll-now`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-messages"] });
      qc.invalidateQueries({ queryKey: ["email-accounts"] });
    },
  });
}

/** Непрочитанные входящие письма — бейдж у пункта «Почта». */
export function useUnreadMail() {
  const poll = usePollingInterval(15_000);
  return useQuery({
    queryKey: ["email-messages", "unread-count"],
    queryFn: () => api.get<{ total: number }>("/email/unread-count"),
    refetchInterval: poll,
  });
}

export function useEmailMessages(params: {
  direction?: "incoming" | "outgoing";
  q?: string;
  is_processed?: boolean;
  is_read?: boolean;
  in_trash?: boolean;
  limit?: number;
  offset?: number;
} = {}) {
  const search = new URLSearchParams();
  if (params.direction) search.set("direction", params.direction);
  if (params.q) search.set("q", params.q);
  if (params.is_processed !== undefined) search.set("is_processed", String(params.is_processed));
  if (params.is_read !== undefined) search.set("is_read", String(params.is_read));
  if (params.in_trash) search.set("in_trash", "true");
  search.set("limit", String(params.limit ?? 50));
  search.set("offset", String(params.offset ?? 0));
  return useQuery({
    queryKey: ["email-messages", params],
    queryFn: () => api.get<EmailMessageSummary[]>(`/email/messages${search.toString() ? `?${search}` : ""}`),
    refetchInterval: 15_000,
  });
}

/** Убрать письмо в корзину. Из базы оно не исчезает — можно вернуть. */
export function useDeleteEmailMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/email/messages/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-messages"] });
    },
  });
}

export function useRestoreEmailMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/email/messages/${id}/restore`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-messages"] });
    },
  });
}

/** Вернуть исходящее письмо в очередь доставки (после «не доставлено»). */
export function useResendEmailMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ delivery_status: string }>(`/email/messages/${id}/resend`, {}),
    onSuccess: (_result, id) => {
      void qc.invalidateQueries({ queryKey: ["email-messages"] });
      void qc.invalidateQueries({ queryKey: ["email-message", id] });
    },
  });
}

/** Что именно уйдёт в корзину при чистке мусорных писем. */
export function useMailCleanupPreview(enabled: boolean) {
  return useQuery({
    queryKey: ["email-messages", "cleanup-preview"],
    queryFn: () => api.get<MailCleanupPreview>("/email/messages/cleanup-test/preview"),
    enabled,
  });
}

export function useCleanupTestMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MailCleanupResult>("/email/messages/cleanup-test", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-messages"] });
    },
  });
}

/** Корзина почты: сколько писем и с какого времени. Ключ под
 *  ["email-messages"], чтобы удаление и возврат писем его обновляли. */
export function useMailTrash(enabled: boolean) {
  return useQuery({
    queryKey: ["email-messages", "trash-summary"],
    queryFn: () => api.get<{ total: number; oldest: string | null }>("/email/trash"),
    enabled,
  });
}

/** Очистить корзину навсегда (только админ). */
export function useEmptyMailTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MailCleanupResult>("/email/trash/empty", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-messages"] });
    },
  });
}

export function useEmailMessage(id: string | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["email-message", id],
    queryFn: async () => {
      const message = await api.get<EmailMessageFull>(`/email/messages/${id}`);
      // Открытие письма помечает его прочитанным на сервере. Счётчик в меню
      // и вкладка «Непрочитанные» должны погаснуть сразу, а не через
      // интервал опроса — иначе кажется, что письмо не открылось.
      void qc.invalidateQueries({ queryKey: ["email-messages"] });
      return message;
    },
    enabled: !!id,
  });
}

/** Привязать письмо к уже существующей закупке.
 *
 *  Автоматическая привязка не гадает, когда у поставщика несколько
 *  активных закупок: ответ про болты не должен уезжать в закупку насоса.
 *  Тогда решает человек — этим хуком.
 */
export function useAttachEmailToProcess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, processId }: { id: string; processId: string }) =>
      api.post<{ ok: boolean; kind: string; amount: number | null; actions: string[] }>(
        `/email/messages/${id}/attach-to-process`,
        { process_id: processId },
      ),
    onSuccess: (_data, { processId }) => {
      qc.invalidateQueries({ queryKey: ["email"] });
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useCreateRequestFromEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: EmailCreateRequestFromEmail;
    }) =>
      api.post<{ process_id: string; items_extracted: number; attachments_available: number }>(
        `/email/messages/${id}/create-request`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-messages"] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useNotifySupplier() {
  return useMutation({
    mutationFn: ({
      processId,
      body,
    }: {
      processId: string;
      body: { subject?: string; body?: string };
    }) =>
      api.post<{ sent_to: string; subject: string }>(
        `/email/processes/${processId}/notify-supplier`,
        body,
      ),
  });
}

export function useSendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      processId,
      body,
    }: {
      processId: string;
      body: { subject?: string; body?: string };
    }) =>
      api.post<{
        ok: boolean;
        sent_to: string;
        subject: string;
        message_id: string;
        process_id: string;
      }>(
        `/email/processes/${processId}/send-request`,
        body,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
      qc.invalidateQueries({ queryKey: ["process-outbox", vars.processId] });
    },
  });
}

// ---- КП поставщика (fixed_supplier portal-first) ----
export interface SupplierOffer {
  offer_id: string;
  item_id: string;
  item_name: string;
  supplier_name: string;
  price: number;
  currency: string;
  delivery_date: string;
  comment: string | null;
  status: string;
  source: "portal" | "purchaser_manual";
  created_at: string;
}

export function useSupplierOffers(processId: string) {
  return useQuery({
    queryKey: ["supplier-offers", processId],
    queryFn: () => api.get<SupplierOffer[]>(`/processes/${processId}/supplier-offers`),
  });
}

export function useAcceptSupplierOffers(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Process>(`/processes/${processId}/accept-supplier-offers`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: ["supplier-offers", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useManualSupplierOffer(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      items: Array<{ item_id: string; price_total: number; delivery_date?: string; comment?: string }>;
      note?: string;
    }) => api.post<Process>(`/processes/${processId}/manual-supplier-offer`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: ["supplier-offers", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useSendAddendum() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (processId: string) =>
      api.post<{
        ok: boolean;
        skipped?: string;
        sent_to?: string;
        message_id?: string;
        new_items?: number;
        new_attachments?: number;
        process_id: string;
      }>(`/email/processes/${processId}/send-addendum`),
    onSuccess: (_d, processId) => {
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
      qc.invalidateQueries({ queryKey: ["process-outbox", processId] });
    },
  });
}

// =====================================================================
//                  Заявки руководителя подразделения (модуль «Request»)
// =====================================================================

export const REQUESTS_KEY = ["requests"] as const;
export const APPROVAL_ROUTES_KEY = ["approval-routes"] as const;
export const REQUEST_TYPE_ROUTINGS_KEY = ["request-type-routings"] as const;

// ---- /requests/mine ----
export function useMyRequests(
  params: { status?: string; limit?: number } = {},
  options?: { enabled?: boolean },
) {
  const { user, token } = useAuth();
  const poll = usePollingInterval(5_000);
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  search.set("limit", String(params.limit ?? 100));
  return useQuery({
    queryKey: ["requests", "mine", user?.id, user?.role, params],
    queryFn: ({ signal }) => getForSession<Process[]>(`/requests/mine?${search}`, token, signal),
    refetchInterval: poll,
    enabled: !!user && (options?.enabled ?? true),
  });
}

export function useRequest(id: string | undefined) {
  const { user, token } = useAuth();
  const poll = usePollingInterval(5_000);
  return useQuery({
    queryKey: ["request", id, user?.id, user?.role],
    queryFn: ({ signal }) => getForSession<Process>(`/requests/${id}`, token, signal),
    enabled: !!id && !!user,
    refetchInterval: poll,
  });
}

export function useRequestsToApprove(params: { limit?: number } = {}) {
  const { user, token } = useAuth();
  const poll = usePollingInterval(5_000);
  const search = new URLSearchParams();
  search.set("limit", String(params.limit ?? 100));
  return useQuery({
    queryKey: ["requests", "to-approve", user?.id, user?.role, params],
    queryFn: ({ signal }) => getForSession<Process[]>(`/requests/to-approve?${search}`, token, signal),
    refetchInterval: poll,
    enabled: !!user,
  });
}

// ---- Заявки, по которым текущий пользователь уже принял решение ----
export type DecidedByMeFilter = "all" | "approved" | "rejected" | "returned";

export function useDecidedByMe(
  decision: DecidedByMeFilter = "all",
  params: { limit?: number } = {},
) {
  const { user, token } = useAuth();
  const search = new URLSearchParams();
  search.set("decision", decision);
  search.set("limit", String(params.limit ?? 100));
  return useQuery({
    queryKey: ["requests", "decided-by-me", decision, user?.id, user?.role, params],
    queryFn: ({ signal }) => getForSession<Process[]>(`/requests/decided-by-me?${search}`, token, signal),
    refetchInterval: 10_000,
    enabled: !!user,
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RequestCreate) =>
      api.post<Process>(`/requests`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useUpdateRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RequestCreate) =>
      api.patch<Process>(`/requests/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useSubmitRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (comment?: string) =>
      api.post<Process>(`/requests/${id}/submit`, comment ? { comment } : {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useCancelRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<Process>(
        `/requests/${id}/cancel?reason=${encodeURIComponent(reason)}`,
        null,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useDeleteRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.del<void>(`/requests/${id}?reason=${encodeURIComponent(reason)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.removeQueries({ queryKey: ["request", id] });
    },
  });
}

// ---- действия по шагам маршрута ----
export function useApproveStep(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StepDecisionRequest = {}) =>
      api.post<Process>(`/requests/${id}/steps/approve`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

/** Отозвать своё согласование — пока следующий шаг не решён. */
export function useUndoStep(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Process>(`/requests/${id}/steps/undo`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

/** Администратор: вернуть согласование на пройденный шаг. */
export function useRollbackStep(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { position: number; reason: string }) =>
      api.post<Process>(`/requests/${id}/steps/rollback`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useRejectStep(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StepDecisionRequest) =>
      api.post<Process>(`/requests/${id}/steps/reject`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useClarifyStep(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StepDecisionRequest = {}) =>
      api.post<Process>(`/requests/${id}/steps/clarify`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useReturnRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StepDecisionRequest) =>
      api.post<Process>(`/requests/${id}/steps/return`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useReassignStep(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StepDecisionRequest) =>
      api.post<Process>(`/requests/${id}/steps/reassign`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useSubmitStockCheckResult(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StockCheckResultRequest) =>
      api.post<Process>(`/requests/${id}/stock-check-result`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useSubmitContractResult(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContractResultRequest) =>
      api.post<Process>(`/requests/${id}/contract-result`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

// ---- Список согласованных заявок, готовых к ручной конверсии ----
export function useEligibleRequests(params: { limit?: number } = {}) {
  const search = new URLSearchParams();
  search.set("limit", String(params.limit ?? 50));
  return useQuery({
    queryKey: ["requests", "eligible-for-conversion", params],
    queryFn: () =>
      api.get<Process[]>(
        `/requests/eligible-for-conversion${search.toString() ? `?${search}` : ""}`,
      ),
    refetchInterval: 10_000,
  });
}

/** Доска заявок закупщика: согласованные / выполненные / отменённые.
 *
 *  Раньше согласованные заявки были видны только в модальном окне кнопки
 *  «Из заявки», а завершённые и отменённые — нигде.
 */
export function useRequestBoard(params: {
  tab: "approved" | "completed" | "cancelled";
  q?: string;
  limit?: number;
}) {
  const search = new URLSearchParams();
  search.set("tab", params.tab);
  if (params.q) search.set("q", params.q);
  search.set("limit", String(params.limit ?? 50));
  return useQuery({
    queryKey: ["requests", "board", params],
    queryFn: () => api.get<Process[]>(`/requests/board?${search}`),
    refetchInterval: 15_000,
  });
}

// ---- Ручная конверсия согласованной заявки в процесс закупки ----
export function useManualConvertRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      processId,
      payload,
    }: {
      processId: string;
      payload?: ManualConvertRequest;
    }) =>
      api.post<Process>(
        `/requests/${processId}/convert-to-procurement`,
        payload ?? {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["requests", "eligible-for-conversion"] });
    },
  });
}

// ---- Вынос части позиций закупки в отдельную закупку к другому поставщику ----
export function useSplitItemsOut(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SplitItemsOutRequest) =>
      api.post<Process>(`/processes/${processId}/items/split-out`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
      qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

// ---- Разделение согласованной заявки: одна закупка на поставщика ----
export function useSplitRequestToProcurements() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      processId,
      payload,
    }: {
      processId: string;
      payload: SplitToProcurementsRequest;
    }) =>
      api.post<Process[]>(`/requests/${processId}/split-to-procurements`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["requests", "eligible-for-conversion"] });
    },
  });
}

// =====================================================================
//                          Approval Routes (admin)
// =====================================================================

export function useApprovalRoutes(params: { only_active?: boolean } = {}) {
  const search = new URLSearchParams();
  if (params.only_active) search.set("only_active", "true");
  return useQuery({
    queryKey: ["approval-routes", params],
    queryFn: () =>
      api.get<ApprovalRouteSummary[]>(
        `/approval-routes${search.toString() ? `?${search}` : ""}`,
      ),
  });
}

export function useApprovalRoute(id: string | undefined) {
  return useQuery({
    queryKey: ["approval-route", id],
    queryFn: () => api.get<ApprovalRoute>(`/approval-routes/${id}`),
    enabled: !!id,
  });
}

export function useCreateApprovalRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ApprovalRouteCreate) =>
      api.post<ApprovalRoute>(`/approval-routes`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approval-routes"] }),
  });
}

export function useUpdateApprovalRoute(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ApprovalRouteUpdate) =>
      api.patch<ApprovalRoute>(`/approval-routes/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approval-route", id] });
      qc.invalidateQueries({ queryKey: ["approval-routes"] });
    },
  });
}

export function useDeleteApprovalRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ detail: string }>(`/approval-routes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approval-routes"] }),
  });
}

export function useAddStep(routeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ApprovalRouteStepCreate) =>
      api.post(`/approval-routes/${routeId}/steps`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approval-route", routeId] }),
  });
}

export function useUpdateStep(routeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stepId,
      payload,
    }: {
      stepId: string;
      payload: ApprovalRouteStepUpdate;
    }) =>
      api.patch(`/approval-routes/${routeId}/steps/${stepId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approval-route", routeId] }),
  });
}

export function useDeleteStep(routeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stepId: string) =>
      api.del(`/approval-routes/${routeId}/steps/${stepId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approval-route", routeId] }),
  });
}

export function useReorderSteps(routeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ApprovalRouteReorder) =>
      api.post(`/approval-routes/${routeId}/reorder`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approval-route", routeId] }),
  });
}

// =====================================================================
//                       Request Type Routings (admin)
// =====================================================================

export function useRequestTypeRoutings() {
  return useQuery({
    queryKey: ["request-type-routings"],
    queryFn: () => api.get<RequestTypeRouting[]>(`/approval-routes/bindings/all`),
  });
}

export function useApprovalRoutePreview(processType: string, department: string, routeId: string) {
  return useQuery({
    queryKey: ["approval-routes", "preview", processType, department, routeId],
    queryFn: () => api.post<{
      source: "department" | "type" | "default" | "none";
      route: { name: string; steps: Array<{ step_id: string; name: string }> } | null;
      warnings: string[];
      can_create: boolean;
      proposed_route_name: string | null;
    }>("/approval-routes/preview/resolve", {
      process_type: processType, department_code: department || null,
      candidate_route_id: routeId || null,
    }),
  });
}

export function useCreateBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RequestTypeRoutingCreate) =>
      api.post(`/approval-routes/bindings`, payload),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ["request-type-routings"] }),
      qc.invalidateQueries({ queryKey: ["approval-routes", "preview"] }),
    ]),
  });
}

export function useUpdateBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: RequestTypeRoutingUpdate;
    }) => api.patch(`/approval-routes/bindings/${id}`, payload),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ["request-type-routings"] }),
      qc.invalidateQueries({ queryKey: ["approval-routes", "preview"] }),
    ]),
  });
}

export function useDeleteBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/approval-routes/bindings/${id}`),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ["request-type-routings"] }),
      qc.invalidateQueries({ queryKey: ["approval-routes", "preview"] }),
    ]),
  });
}

export const ADMIN_USERS_KEY = ["admin-users"] as const;

export interface AdminUserOption {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  department_code: string | null;
  department_name: string | null;
}

export function useAdminUsers(params: { q?: string; only_active?: boolean } = {}) {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.only_active) search.set("only_active", "true");
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, params],
    queryFn: () =>
      api.get<AdminUserOption[]>(
        `/auth/users${search.toString() ? `?${search}` : ""}`,
      ),
    staleTime: 30_000,
  });
}


// =====================================================================
//                   Product Categories (реальные категории закупок)
// =====================================================================

export const PRODUCT_CATEGORIES_KEY = ["product-categories"] as const;

export function useProductCategories(
  params: { active_only?: boolean; include_test?: boolean } = {},
) {
  const search = new URLSearchParams();
  if (params.active_only) search.set("active_only", "true");
  if (params.include_test) search.set("include_test", "true");
  return useQuery({
    queryKey: ["product-categories", params],
    queryFn: () =>
      api.get<ProductCategory[]>(
        `/product-categories${search.toString() ? `?${search}` : ""}`,
      ),
  });
}

export function useProductCategory(code: string | undefined) {
  return useQuery({
    queryKey: ["product-category", code],
    queryFn: () => api.get<ProductCategory>(`/product-categories/${code}`),
    enabled: !!code,
  });
}

export interface AutotagPreview {
  category_code: string | null;
  category_name: string | null;
  matched_text: string | null;
  matched_by: "synonym" | "name" | null;
}

/** Что справочник сделает с таким названием позиции — и по какому слову.
 *  Только справочник, без ИИ: вопрос в том, как настроены синонимы. */
export function useAutotagPreview() {
  return useMutation({
    mutationFn: (name: string) =>
      api.post<AutotagPreview>("/product-categories/autotag-preview", { name }),
  });
}

/** Подсказка тега позиции по названию (автоподстановка в форме заявки). */
export function autotagSuggest(name: string) {
  return api.get<{ name: string; category_code: string | null }>(
    `/product-categories/autotag-suggest?name=${encodeURIComponent(name)}`,
  );
}

export function useCreateProductCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductCategoryCreate) =>
      api.post<ProductCategory>(`/product-categories`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-categories"] }),
  });
}

export function useUpdateProductCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload }: { code: string; payload: ProductCategoryUpdate }) =>
      api.patch<ProductCategory>(`/product-categories/${code}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-categories"] }),
  });
}

export function useDeleteProductCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.del(`/product-categories/${code}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-categories"] }),
  });
}


// =====================================================================
//              Ручное продвижение процесса (manual_procurement и fallback)
// =====================================================================

export interface ManualAdvanceRequest {
  event: string;
  note?: string | null;
  supplier_id?: string | null;
  invoice_amount?: number | null;
  invoice_items?: Array<Record<string, unknown>> | null;
}

export interface SupplierSuggestion {
  supplier_id: string;
  name: string;
  email: string | null;
  /** Позиции закупки, которые у него уже покупали. */
  matched_items: string[];
  items_total: number;
  invoices: number;
  last_date: string | null;
}

/** У кого покупали такие позиции раньше — для шага выбора поставщика. */
export function useSupplierSuggestions(processId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["process", processId, "supplier-suggestions"],
    queryFn: () => api.get<SupplierSuggestion[]>(`/processes/${processId}/supplier-suggestions`),
    enabled,
    staleTime: 60_000,
  });
}

export function useManualAdvance(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ManualAdvanceRequest) =>
      api.post<Process>(`/processes/${processId}/manual-advance`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useSetSupplier(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId }: { supplierId: string }) =>
      api.post<Process>(`/processes/${processId}/set-supplier?supplier_id=${supplierId}`, null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useChangeProcessType(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ processType, resetToIntake }: { processType: string; resetToIntake?: boolean }) =>
      api.post<Process>(`/processes/${processId}/change-type`, {
        process_type: processType,
        reset_to_intake: resetToIntake ?? true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

// ---- Перевести процесс в ручной режим (manual_procurement) ----
export function useSwitchToManual(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { reason?: string | null } = {}) =>
      api.post<Process>(`/processes/${processId}/switch-to-manual`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

export function useSetInvoice(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      amount: number;
      items?: Array<Record<string, unknown>>;
      number?: string | null;
      date?: string | null;
      note?: string | null;
    }) =>
      api.post<Process>(`/processes/${processId}/set-invoice`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

// ---- Разобрать счёт заново: после неверного разбора, до решения по счёту ----
export function useReparseInvoice(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Process>(`/processes/${processId}/action/reparse-invoice`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

// ---- Договор (ручной режим): номер, дата, сумма ----
export function useSetContract(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      number?: string | null;
      date?: string | null;
      amount?: number | null;
      note?: string | null;
    }) =>
      api.post<Process>(`/processes/${processId}/set-contract`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process", processId] });
      qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
  });
}

// ---- Универсальное создание процесса закупки (выбор маршрута) ----
// Используется для кнопки «Создать процесс» на странице /processes —
// закупщик выбирает один из трёх маршрутов (ручной / фикс. поставщик / тендер).
// (useCreateProcess определён выше)

// ---------------------- analytics ----------------------
export const ANALYTICS_KEY = ["analytics"] as const;

function filterSearch(filter: AnalyticsFilter = {}): URLSearchParams {
  const search = new URLSearchParams();
  if (filter.date_from) search.set("date_from", filter.date_from);
  if (filter.date_to) search.set("date_to", filter.date_to);
  if (filter.department) search.set("department", filter.department);
  if (filter.supplier_id) search.set("supplier_id", filter.supplier_id);
  if (filter.legal_entity_id) search.set("legal_entity_id", filter.legal_entity_id);
  return search;
}

export function useAnalyticsSummary(filter: AnalyticsFilter = {}) {
  const search = filterSearch(filter);
  return useQuery({
    queryKey: [...ANALYTICS_KEY, "summary", filter],
    queryFn: () => api.get<AnalyticsSummary>(`/analytics/summary${search.toString() ? `?${search}` : ""}`),
    refetchInterval: 30_000,
  });
}

export function useRisks(
  filter: AnalyticsFilter & { period_days?: number; price_threshold_pct?: number } = {},
) {
  const search = filterSearch(filter);
  if (filter.period_days) search.set("period_days", String(filter.period_days));
  if (filter.price_threshold_pct !== undefined)
    search.set("price_threshold_pct", String(filter.price_threshold_pct));
  return useQuery({
    queryKey: [...ANALYTICS_KEY, "risks", filter],
    queryFn: () => api.get<RisksResponse>(`/analytics/risks${search.toString() ? `?${search}` : ""}`),
    refetchInterval: 30_000,
  });
}

export function usePriceVariance(
  filter: AnalyticsFilter & { period_days?: number; threshold_pct?: number } = {},
) {
  const search = filterSearch(filter);
  if (filter.period_days) search.set("period_days", String(filter.period_days));
  if (filter.threshold_pct !== undefined) search.set("threshold_pct", String(filter.threshold_pct));
  return useQuery({
    queryKey: [...ANALYTICS_KEY, "price-variance", filter],
    queryFn: () =>
      api.get<PriceVarianceResponse>(`/analytics/price-variance${search.toString() ? `?${search}` : ""}`),
    refetchInterval: 30_000,
  });
}

export function useSpend(
  group_by: "category" | "supplier" | "department" | "month" | "legal_entity" = "category",
  filter: AnalyticsFilter = {},
) {
  const search = filterSearch(filter);
  search.set("group_by", group_by);
  return useQuery({
    queryKey: [...ANALYTICS_KEY, "spend", group_by, filter],
    queryFn: () => api.get<SpendResponse>(`/analytics/spend?${search}`),
    refetchInterval: 30_000,
  });
}

export function useSavings(filter: AnalyticsFilter = {}) {
  const search = filterSearch(filter);
  return useQuery({
    queryKey: [...ANALYTICS_KEY, "savings", filter],
    queryFn: () => api.get<SavingsResponse>(`/analytics/savings${search.toString() ? `?${search}` : ""}`),
    refetchInterval: 30_000,
  });
}

export function usePriceHistory(item: string | null) {
  return useQuery({
    queryKey: [...ANALYTICS_KEY, "price-history", item],
    queryFn: () => api.get<PriceHistoryResponse>(`/analytics/price-history?item=${encodeURIComponent(item ?? "")}`),
    enabled: !!item && item.length >= 2,
  });
}

export interface OutboxEntry {
  recipient: string;
  subject: string;
  sent_at: string;
  source: string;
}

export function useProcessOutbox(id: string | undefined) {
  return useQuery({
    queryKey: ["process-outbox", id],
    queryFn: () => api.get<OutboxEntry[]>(`/processes/${id}/outbox`),
    enabled: !!id,
  });
}

// =====================================================================
//                 Units (справочник единиц измерения)
// =====================================================================

export const UNITS_KEY = ["units"] as const;

export function useUnits(params: { active_only?: boolean } = {}) {
  const search = new URLSearchParams();
  search.set("active_only", String(params.active_only ?? true));
  return useQuery({
    queryKey: [...UNITS_KEY, params],
    queryFn: () => api.get<Unit[]>(`/units?${search}`),
  });
}

export function useCreateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UnitCreate) => api.post<Unit>(`/units`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: UNITS_KEY }),
  });
}

export function useUpdateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload }: { code: string; payload: UnitUpdate }) =>
      api.patch<Unit>(`/units/${encodeURIComponent(code)}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: UNITS_KEY }),
  });
}

export function useDeleteUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.del(`/units/${encodeURIComponent(code)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: UNITS_KEY }),
  });
}

// ---- Общие настройки приложения (admin управляет) ----
export const APP_SETTINGS_KEY = ["app-settings"] as const;

export interface AppSetting {
  key: string;
  value: string;
}

export function useAppSettings() {
  return useQuery({
    queryKey: APP_SETTINGS_KEY,
    queryFn: () => api.get<AppSetting[]>("/settings"),
  });
}

export function useUpdateAppSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put<AppSetting>(`/settings/${encodeURIComponent(key)}`, { value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: APP_SETTINGS_KEY }),
  });
}

export function useCloneRequest(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Process>(`/requests/${id}/clone`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requests"] }),
  });
}

export function useInviteSupplierToPortal() {
  return useMutation({
    mutationFn: (supplierId: string) =>
      api.post<{
        ok: boolean;
        login: string;
        temp_password: string;
        email_sent: boolean;
        detail: string;
      }>(`/suppliers/${supplierId}/invite-portal`),
  });
}

/* ==========================================================================
   Объекты и шаблоны процесса закупки
   ========================================================================== */

export const OBJECTS_KEY = ["objects"] as const;
export const PROCESS_TEMPLATES_KEY = ["process-templates"] as const;

export function useObjects(params: { active_only?: boolean; include_test?: boolean } = {}) {
  const search = new URLSearchParams();
  if (params.active_only) search.set("active_only", "true");
  if (params.include_test) search.set("include_test", "true");
  return useQuery({
    queryKey: [...OBJECTS_KEY, params],
    queryFn: () =>
      api.get<ProcurementObject[]>(`/objects${search.toString() ? `?${search}` : ""}`),
  });
}

function invalidateObjects(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: OBJECTS_KEY });
  // Отделы и шаблоны показывают название объекта.
  void qc.invalidateQueries({ queryKey: DEPARTMENTS_KEY });
  void qc.invalidateQueries({ queryKey: PROCESS_TEMPLATES_KEY });
}

export function useCreateObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProcurementObjectCreate) => api.post<ProcurementObject>("/objects", body),
    onSuccess: () => invalidateObjects(qc),
  });
}

export function useUpdateObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProcurementObjectUpdate }) =>
      api.patch<ProcurementObject>(`/objects/${id}`, body),
    onSuccess: () => invalidateObjects(qc),
  });
}

export function useDeleteObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/objects/${id}`),
    onSuccess: () => invalidateObjects(qc),
  });
}

export function useFlowCatalog() {
  return useQuery({
    queryKey: [...PROCESS_TEMPLATES_KEY, "catalog"],
    queryFn: () => api.get<FlowScenarioSpec[]>("/process-templates/catalog"),
    staleTime: 5 * 60_000,
  });
}

export function useProcessTemplates(params: { object_id?: string; base_type?: string } = {}) {
  const search = new URLSearchParams();
  if (params.object_id) search.set("object_id", params.object_id);
  if (params.base_type) search.set("base_type", params.base_type);
  return useQuery({
    queryKey: [...PROCESS_TEMPLATES_KEY, params],
    queryFn: () =>
      api.get<ProcessTemplate[]>(`/process-templates${search.toString() ? `?${search}` : ""}`),
  });
}

export function useTemplatePreview() {
  return useMutation({
    mutationFn: (body: { base_type: string; steps: TemplateStep[] }) =>
      api.post<FlowPreview>("/process-templates/preview", body),
  });
}

export function useCreateProcessTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProcessTemplateCreate) => api.post<ProcessTemplate>("/process-templates", body),
    onSuccess: () => invalidateObjects(qc),
  });
}

export function useUpdateProcessTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProcessTemplateUpdate }) =>
      api.patch<ProcessTemplate>(`/process-templates/${id}`, body),
    onSuccess: () => invalidateObjects(qc),
  });
}

export function useDeleteProcessTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/process-templates/${id}`),
    onSuccess: () => invalidateObjects(qc),
  });
}
